import { Fragment, useEffect, useState } from 'react'
import { Button, Input } from '@heroui/react'
import { api } from '../../lib/api'
import {
  queueHint,
  queueProblem,
  rangeLabel,
  servicesElsewhere,
  type BasicQueue,
  type BasicQueueIn,
  type BasicService,
} from '../../lib/queueConfig'
import { nameIn } from '../../lib/staffText'
import { Icon } from '../../ui/icons'
import { useMayRead } from '../../ui/Session'
import { EmptyState } from '../../ui/parts'
import { useStaffText } from '../../ui/StaffText'
import { Loading } from '../Loading'
import { Confirm, useFlash } from '../sectionKit'
import { IconTile, InnerPage, ListCard, ListGroupRow, ListRow, ListTable, NameCell, Notice, PageHead, RowArrow, toneOf, useOpenRecord } from '../settingsKit'
import { Tick } from '../setupKit'
import { NotBasicNote, ReadOnlyNote, refusalText, useQueueScreen } from './shared'
import '../../styles/branch.css'

/** Queue management › Queues: where customers wait, each queue with its services and target. */
export function BasicQueues({
  organisation,
  readOnly,
  onAdvanced,
}: {
  organisation?: string
  readOnly: boolean
  onAdvanced: () => void
}) {
  const { lang, say } = useStaffText()
  const { screen, error, write } = useQueueScreen(organisation)
  const [catalogue, setCatalogue] = useState<BasicService[]>([])
  const [find, setFind] = useState('')
  const { flash, toast } = useFlash()
  const readsServices = useMayRead('services')
  const editable = Boolean(screen?.editable) && !readOnly
  // A queue opens on its own page (#basicQueues/<id>, #basicQueues/new); read-only opens none.
  const rec = useOpenRecord(
    (screen?.setups ?? []).flatMap((s) => s.queues),
    (q) => q.id,
    Boolean(screen),
  )
  const editing: BasicQueue | false | undefined = editable && (rec.isNew ? blankQueue() : rec.row)

  // Without the Basic API the catalogue names the services.
  useEffect(() => {
    if (!readsServices || !screen || screen.services) return
    api
      .catalogueServices(organisation)
      .then((r) => setCatalogue(r.services.map((s) => ({ id: s.id, code: s.code, name: s.name, active: s.active, queueId: '' }))))
      .catch(() => setCatalogue([]))
  }, [organisation, readsServices, screen])

  const services = screen?.services ?? catalogue
  const serviceName = (id: string) => {
    const s = services.find((x) => x.id === id)
    return s ? nameIn(s.name, lang, s.code) : ''
  }
  const f = find.trim().toLocaleLowerCase()
  const matches = (q: BasicQueue) => !f || `${nameIn(q.name, lang, q.code)} ${q.letter} ${q.code}`.toLocaleLowerCase().includes(f)
  const setups = screen?.setups ?? []
  const total = setups.reduce((n, s) => n + s.queues.length, 0)
  const at = { baseRevision: screen?.revision, ...(organisation ? { organisation } : {}) }

  const shown = setups.map((setup) => ({ setup, rows: setup.queues.filter(matches) })).filter((x) => x.rows.length > 0)

  if (editing) {
    return (
      <>
        {toast}
        <QueueSheet
          key={rec.code}
          queue={editing}
          queues={screen?.setups[0]?.queues ?? []}
          services={services}
          onClose={rec.close}
          onSave={async (draft) => {
            await write(api.saveBasicQueue(editing.id, draft, at))
            rec.close()
            flash(say('catalogue.queues.saved'))
          }}
          onDelete={
            editing.id
              ? async () => {
                  await write(api.deleteBasicQueue(editing.id, at))
                  rec.close()
                  flash(say('catalogue.queues.deleted'))
                }
              : undefined
          }
        />
      </>
    )
  }

  return (
    <>
      <PageHead
        title={say('admin.section.queues')}
        line={say('settings.queues.line')}
        actions={
          editable ? (
            <Button variant="primary" onPress={() => rec.open()}>
              <Icon name="plus" size={16} /> {say('catalogue.queues.add')}
            </Button>
          ) : undefined
        }
      />
      {toast}
      {screen && screen.mode !== 'basic' && <ReadOnlyNote onAdvanced={onAdvanced} />}
      {screen?.mode === 'basic' && screen.problems.length > 0 && <NotBasicNote problems={screen.problems} />}
      {error && <Notice>{error}</Notice>}

      <ListCard
        title={say('admin.section.queues')}
        count={screen ? say('settings.queues.count', { n: total }) : undefined}
        search={find}
        onSearch={setFind}
        searchLabel={say('catalogue.queues.find')}
      >
        {!screen && <Loading />}
        {screen && total === 0 && !error && <EmptyState icon={<Icon name="grid" size={20} />} title={say('catalogue.queues.none')} />}
        {total > 0 && shown.length === 0 && <EmptyState title={say('catalogue.services.noMatch', { q: find })} />}
        {shown.length > 0 && (
          <ListTable
            label={say('admin.section.queues')}
            cols={`minmax(240px, 1.8fr) 120px 110px ${editable ? '16px' : '0px'}`}
            head={[say('settings.queues.col.queue'), say('settings.queues.col.numbers'), say('settings.queues.col.target'), '']}
          >
            {shown.map(({ setup, rows }) => (
              <Fragment key={setup.id}>
                {setups.length > 1 && <ListGroupRow name={setup.name} />}
                {rows.map((q) => (
                  <ListRow key={q.id} onOpen={editable ? () => rec.open(q) : undefined}>
                    <NameCell
                      tile={<IconTile text={q.letter || q.code.slice(0, 1)} tone={toneOf(q.id)} />}
                      name={nameIn(q.name, lang, q.code)}
                      sub={q.serviceIds.map(serviceName).filter(Boolean).join(' · ') || say('catalogue.queues.noServices')}
                    />
                    <span role="cell" className="set-num">
                      {rangeLabel(q)}
                    </span>
                    <span role="cell" className="set-num">
                      {say('catalogue.queues.target', { n: q.targetMin })}
                    </span>
                    {editable ? <RowArrow /> : <span />}
                  </ListRow>
                ))}
              </Fragment>
            ))}
          </ListTable>
        )}
      </ListCard>
    </>
  )
}

const blankQueue = (): BasicQueue => ({
  id: '',
  code: '',
  name: {},
  letter: '',
  firstNumber: 1,
  lastNumber: 999,
  targetMin: 15,
  serviceIds: [],
})

function QueueSheet({
  queue,
  queues,
  services,
  onClose,
  onSave,
  onDelete,
}: {
  queue: BasicQueue
  queues: BasicQueue[]
  services: BasicService[]
  onClose: () => void
  onSave: (draft: BasicQueueIn) => Promise<void>
  onDelete?: () => Promise<void>
}) {
  const { lang, say } = useStaffText()
  const [draft, setDraft] = useState<BasicQueueIn>(() => {
    const { id: _id, code: _code, ...rest } = queue
    return { ...rest, name: { ...rest.name, en: rest.name.en ?? nameIn(rest.name, lang, '') } }
  })
  const [error, setError] = useState('')
  const [busy, setBusy] = useState(false)
  // An edit answers the last refusal, so it goes with it.
  const set = (patch: Partial<BasicQueueIn>) => {
    setDraft((d) => ({ ...d, ...patch }))
    setError('')
  }
  const hint = queueHint(draft)
  const elsewhere = servicesElsewhere(queues, queue.id)
  const toggle = (id: string) =>
    set({ serviceIds: draft.serviceIds.includes(id) ? draft.serviceIds.filter((x) => x !== id) : [...draft.serviceIds, id] })
  const num = (typed: string) => Math.max(0, Math.round(Number(typed) || 0))

  const act = async (fn: () => Promise<void>) => {
    setBusy(true)
    setError('')
    try {
      await fn()
    } catch (err) {
      setError(refusalText(err, lang, say, queues))
    } finally {
      setBusy(false)
    }
  }

  return (
    <InnerPage
      back={say('admin.section.queues')}
      title={queue.id ? nameIn(queue.name, lang, queue.code) : say('catalogue.queues.new')}
      onClose={onClose}
      onSubmit={() => void act(() => onSave(draft))}
      submitLabel={say('common.save')}
      valid={queueProblem(draft) === '' && draft.serviceIds.length > 0}
      busy={busy}
      error={error}
      extra={
        hint ? (
          say(hint)
        ) : onDelete ? (
          <Confirm
            ask={say('common.delete')}
            question={say('catalogue.queues.deleteAsk')}
            yes={say('common.delete')}
            keep={say('ui.keepEditing')}
            busy={busy}
            onYes={() => void act(onDelete)}
          />
        ) : undefined
      }
    >
      <div className="admin-pair">
        <label className="field">
          <span>{say('catalogue.queues.name')}</span>
          <Input
            fullWidth
            value={draft.name.en ?? ''}
            placeholder={say('catalogue.queues.namePlaceholder')}
            onChange={(e) => set({ name: { ...draft.name, en: e.target.value } })}
          />
        </label>
        <label className="field">
          <span>{say('catalogue.queues.prefix')}</span>
          <Input
            fullWidth
            maxLength={4}
            value={draft.letter}
            placeholder="A"
            onChange={(e) => set({ letter: e.target.value.toUpperCase().replace(/[^A-Z]/g, '') })}
          />
        </label>
      </div>
      <div className="admin-pair">
        <fieldset className="field">
          <legend>{say('catalogue.queues.numbers')}</legend>
          <span className="admin-inline">
            <Input
              className="setup-num"
              type="number"
              min={1}
              max={9999}
              aria-label={say('catalogue.queues.first')}
              value={draft.firstNumber}
              onChange={(e) => set({ firstNumber: num(e.target.value) })}
            />
            <span aria-hidden="true">–</span>
            <Input
              className="setup-num"
              type="number"
              min={1}
              max={9999}
              aria-label={say('catalogue.queues.last')}
              value={draft.lastNumber}
              onChange={(e) => set({ lastNumber: num(e.target.value) })}
            />
          </span>
        </fieldset>
        <label className="field">
          <span>{say('catalogue.queues.targetMin')}</span>
          <Input
            className="setup-num"
            type="number"
            min={1}
            max={240}
            value={draft.targetMin}
            onChange={(e) => set({ targetMin: num(e.target.value) })}
          />
        </label>
      </div>
      <fieldset className="field">
        <legend>{say('catalogue.queues.services')}</legend>
        <div className="setup-ticks">
          {services
            .filter((s) => s.active || draft.serviceIds.includes(s.id))
            .map((s) => {
              const other = elsewhere.get(s.id)
              return (
                <Tick key={s.id} checked={draft.serviceIds.includes(s.id)} disabled={Boolean(other)} onChange={() => toggle(s.id)}>
                  {nameIn(s.name, lang, s.code)}
                  {other && <small>{say('catalogue.queues.inQueue', { name: nameIn(other.name, lang, other.code) })}</small>}
                </Tick>
              )
            })}
        </div>
      </fieldset>
    </InnerPage>
  )
}
